/**
 * Reconciled from the invoice (1.72, decisions of 14/10/2026): on the page of
 * an invoice, the movements of the banks (and, for a received invoice, the
 * spending with the cards) that may collect or pay it, ranked with the rules
 * of the reconciliation from the movement. One or more are chosen, each with
 * its part; the result is the same reconciliation made from the movement.
 */
import { z } from 'zod';
import { addDays } from './dates.js';
import { formatMoney, fromCents, toCents } from './money.js';
import { reconciliationProposals } from './reconciliation.js';
import { idSchema, isoDateSchema } from './validation.js';
export const INVOICE_BANK_SOURCES = ['banca', 'carta'];
/** Days before the invoice the period starts by default: advances may come before it. */
export const INVOICE_BANK_DAYS_BEFORE = 30;
/** At most this many movements in one confirmation. */
export const INVOICE_BANK_MAX = 20;
const positiveMoney = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value)))
    .refine((value) => toCents(value) > 0, { message: 'Importo non valido' });
/** The movements chosen, each with the part that goes to the invoice (positive). */
export const invoiceBankLinkSchema = z.object({
    links: z
        .array(z.object({
        source: z.enum(INVOICE_BANK_SOURCES),
        movementId: idSchema,
        amount: positiveMoney,
    }))
        .min(1, { message: 'Scegli almeno un movimento' })
        .max(INVOICE_BANK_MAX)
        .refine((links) => new Set(links.map((link) => link.movementId)).size === links.length, {
        message: 'Un movimento compare due volte',
    }),
});
/** The period of the movements: given, the one proposed when missing, or all with `all`. */
export const invoiceBankQuerySchema = z.object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    all: z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => value === 'true'),
});
/** The period proposed: from 30 days before the invoice to today. */
export function invoiceBankPeriod(invoiceDate, today) {
    return { from: addDays(invoiceDate, -INVOICE_BANK_DAYS_BEFORE), to: today };
}
/**
 * The movements ranked for an invoice: each scored as the reconciliation from
 * the movement would score this invoice, on what of it is still free; one
 * whose free part equals a payment marked by hand proposes that amount. The
 * first is the one to choose when it scores 60 or more, 20 ahead of the next.
 */
export function invoiceBankCandidates(invoice, movements, dues = [], manual = []) {
    const residual = Math.abs(toCents(invoice.residual));
    const sign = invoice.direction === 'emessa' ? 1 : -1;
    const scored = movements.map((movement) => {
        const free = Math.abs(toCents(movement.free));
        const match = manual.find((item) => Math.abs(toCents(item.amount)) === free) ?? null;
        // Scored on what is still free; an invoice marked paid by hand still counts its manual part.
        const [found] = reconciliationProposals({
            amount: fromCents(free * sign),
            description: movement.description,
            counterparty: movement.counterparty,
            bookingDate: movement.date,
        }, [
            {
                ...invoice,
                state: match ? 'aperta' : invoice.state,
                residual: fromCents(match ? Math.max(residual, free) : residual),
            },
        ], new Map([[invoice.id, dues]]));
        const reasons = [...(found?.reasons ?? [])];
        let score = found?.score ?? 0;
        if (match) {
            score += 30;
            reasons.push(invoice.direction === 'emessa'
                ? 'come l’incasso segnato a mano'
                : 'come il pagamento segnato a mano');
        }
        const proposed = match ? free : Math.min(free, residual);
        return {
            ...movement,
            proposed: fromCents(proposed),
            manual: match
                ? { date: match.date, amount: fromCents(Math.abs(toCents(match.amount))) }
                : null,
            score,
            reasons,
            best: false,
        };
    });
    scored.sort((a, b) => b.score - a.score ||
        Math.abs(toCents(a.free) - residual) - Math.abs(toCents(b.free) - residual) ||
        b.date.localeCompare(a.date));
    const [first, second] = scored;
    if (first &&
        toCents(first.proposed) > 0 &&
        first.score >= 60 &&
        (!second || first.score - second.score >= 20)) {
        first.best = true;
    }
    return scored;
}
/**
 * What is wrong with the movements chosen, or null: none gives more than what
 * of it is free, and together they give no more than what is left on the
 * invoice (those that become a payment marked by hand do not add to it).
 */
export function invoiceBankError(residual, chosen) {
    for (const item of chosen) {
        if (toCents(item.amount) <= 0)
            return 'Importo non valido';
        if (toCents(item.amount) > Math.abs(toCents(item.free))) {
            return `Del movimento ${item.label} restano da assegnare solo ${formatMoney(fromCents(Math.abs(toCents(item.free))))}`;
        }
    }
    const total = chosen
        .filter((item) => !item.manual)
        .reduce((sum, item) => sum + toCents(item.amount), 0);
    const left = Math.max(toCents(residual), 0);
    if (total > left) {
        return `Sulla fattura restano ${formatMoney(fromCents(left))}, non ${formatMoney(fromCents(total))}`;
    }
    return null;
}
//# sourceMappingURL=invoice-bank.js.map